import api from "./Api";
import { handleApiError } from "../utils/apiUtils";
import { formatAppointmentDateTime } from "../utils/dateUtils";
import { RATE_TYPE } from "../common/Constant";

const invoiceBaseUrl = process.env.REACT_APP_CASE_URL ?? "";
const invoicePort = api(invoiceBaseUrl);

interface ApiResponse<T> {
  isSuccess: boolean;
  statusCode: number;
  message: string;
  result: T;
  timestamp: string;
}

export interface InvoiceSummaryByClientPayload {
  yearMonth: string;
  invoiceLineItemStatusIds: string;
  teamId: number | null;
  clientId: number | null;
  searchText: string;
  infoCardInvltmStatus: string;
}

export interface InvoiceSummaryByClient {
  clientId: number;
  clientName: string;
  countOfQmeDoctors: number;
  grandTotalNoOfPages: number;
  grandTotalNoOfLines: number;
  grandTotalAmount: number;
}

export interface InvoiceSummaryByClientQme {
  clientId: number;
  clientName: string;
  qmeDoctorContactId?: number;
  qmeDoctorName?: string;
  billingForUserRole?: number;
  userId?: number;
  userInformation?: string;
  countOfCases: number;
  grandTotalNoOfPages: number;
  grandTotalNoOfLines: number;
  grandTotalAmount: number;
}

export interface InvoiceByClientQmeCasePayload {
  yearMonth: string;
  invoiceLineItemStatusIds: string;
  teamId: number | null;
  clientId: number | null;
  qmeDoctorContactId?: number;
  billingForUserRole?: string;
  userId?: number;
  searchText: string;
  infoCardInvltmStatus: string;
}

export const getInvoiceSummaryByClient = async (
  payload: InvoiceSummaryByClientPayload,
): Promise<InvoiceSummaryByClient[]> => {
  try {
    const { data } = await invoicePort.post<
      ApiResponse<InvoiceSummaryByClient[]>
    >("/api/invoice/summary/by-client", payload);

    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      grandTotalAmount: [
        user?.grandTotalAmount &&
          `$ ${Number(user.grandTotalAmount)?.toFixed(2) ?? 0}`,
        user?.grandTotalAmount_INR &&
          `₹ ${Number(user.grandTotalAmount_INR)?.toFixed(2) ?? 0}`,
      ]
        ?.filter(Boolean)
        ?.join(" | "),
    }));

    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceByClientQme = async (
  payload: InvoiceSummaryByClientPayload,
): Promise<InvoiceSummaryByClientQme[]> => {
  try {
    const { data } = await invoicePort.post<
      ApiResponse<InvoiceSummaryByClientQme[]>
    >("/api/invoice/summary/by-client-qme", payload);

    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      grandTotalAmount: [
        user?.grandTotalAmount &&
          `$ ${Number(user.grandTotalAmount)?.toFixed(2) ?? 0}`,
        user?.grandTotalAmount_INR &&
          `₹ ${Number(user.grandTotalAmount_INR)?.toFixed(2) ?? 0}`,
      ]
        ?.filter(Boolean)
        ?.join(" | "),
    }));

    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceByClientQmeCase = async (
  payload: InvoiceByClientQmeCasePayload,
) => {
  try {
    const { data } = await invoicePort.post(
      "/api/invoice/summary/by-client-qme-case",
      payload,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      patientName: user.subjectInfo || "",
      taskName: user.documentTask || "",
      noOfPages: user.totalNoOfPages?.toString() || "0",
      noOfLines: user.totalNoOfLines?.toString() || "0",
      dateUploaded: user.uploadInformation || "",
      rate:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.billingRuleRate ?? 0)?.toFixed(2)}`,
      amount:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.totalAmount ?? 0)?.toFixed(2)}`,
      invoiceStatus: user.invoiceLineItemStatus || "",
      dateOfAppointmentFormat: formatAppointmentDateTime(
        user.dateOfAppointment,
      ),
      rateType: user.billingRuleRateType,
    }));

    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceByClientRole = async (
  payload: InvoiceSummaryByClientPayload,
): Promise<InvoiceSummaryByClientQme[]> => {
  try {
    const { data } = await invoicePort.post<
      ApiResponse<InvoiceSummaryByClientQme[]>
    >("/api/invoice/summary/by-client-role", payload);
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      qmeDoctorName: user.billingForUserRole || "",
      qmeDoctorContactId: user.billingForUserRole || "",
      grandTotalAmount: [
        user?.grandTotalAmount &&
          `$ ${Number(user.grandTotalAmount)?.toFixed(2) ?? 0}`,
        user?.grandTotalAmount_INR &&
          `₹ ${Number(user.grandTotalAmount_INR)?.toFixed(2) ?? 0}`,
      ]
        ?.filter(Boolean)
        ?.join(" | "),
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceByClientRoleCase = async (
  payload: InvoiceByClientQmeCasePayload,
) => {
  try {
    const { data } = await invoicePort.post(
      "/api/invoice/summary/by-client-role-case",
      payload,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      patientName: user.subjectInfo || "",
      taskName: user.qmeDoctorName || "",
      noOfPages: user.totalNoOfPages?.toString() || "0",
      noOfLines: user.totalNoOfLines?.toString() || "0",
      dateUploaded: user.uploadInformation || "",
      rate:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.billingRuleRate ?? 0)?.toFixed(2)}`,
      amount:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.totalAmount ?? 0)?.toFixed(2)}`,
      invoiceStatus: user.invoiceLineItemStatus || "",
      dateOfAppointmentFormat: formatAppointmentDateTime(
        user.dateOfAppointment,
      ),
      rateType: user.billingRuleRateType,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceByClientUser = async (
  payload: InvoiceSummaryByClientPayload,
): Promise<InvoiceSummaryByClientQme[]> => {
  try {
    const { data } = await invoicePort.post<
      ApiResponse<InvoiceSummaryByClientQme[]>
    >("/api/invoice/summary/by-client-user", payload);
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      qmeDoctorName: user.userInformation || "",
      qmeDoctorContactId: user.userId,
      grandTotalAmount: [
        user?.grandTotalAmount &&
          `$ ${Number(user.grandTotalAmount)?.toFixed(2) ?? 0}`,
        user?.grandTotalAmount_INR &&
          `₹ ${Number(user.grandTotalAmount_INR)?.toFixed(2) ?? 0}`,
      ]
        ?.filter(Boolean)
        ?.join(" | "),
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceByClientUserCase = async (
  payload: InvoiceByClientQmeCasePayload,
) => {
  try {
    const { data } = await invoicePort.post(
      "/api/invoice/summary/by-client-role-user",
      payload,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      patientName: user.subjectInfo || "",
      taskName: user.qmeDoctorName || "",
      noOfPages: user.totalNoOfPages?.toString() || "0",
      noOfLines: user.totalNoOfLines?.toString() || "0",
      dateUploaded: user.uploadInformation || "",
      rate:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.billingRuleRate ?? 0)?.toFixed(2)}`,
      amount:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.totalAmount ?? 0)?.toFixed(2)}`,
      invoiceStatus: user.invoiceLineItemStatus || "",
      dateOfAppointmentFormat: formatAppointmentDateTime(
        user.dateOfAppointment,
      ),
      rateType: user.billingRuleRateType,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceStatusList = async () => {
  try {
    const { data } = await invoicePort.get("/api/invoice/status/list");
    const result = data?.result ?? [];
    const filterData = result?.map((item: any) => ({
      value: item.id,
      label: item.statusDescription,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceTeamClientList = async () => {
  try {
    const { data } = await invoicePort.get("/api/invoice/team-client/list");
    const result = data?.result ?? [];
    const filterData = result?.map((item: any) => ({
      value: item.clientId,
      label: item.teamClientName,
      teamId: item.teamId,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceCaseNotes = async (
  subjectQmeScheduleId: number,
  invoiceLineItemId?: number,
) => {
  try {
    const { data } = await invoicePort.get(
      `api/invoice/case-notes?subjectQmeScheduleId=${subjectQmeScheduleId}&invoiceLineItemId=${invoiceLineItemId}`,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((item: any, index: number) => ({
      SrNo: index + 1,
      ...item,
      uploadedOn: `${formatAppointmentDateTime(item.uploadedOn)} | ${item.uploadedBy}`,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const generateInvoiceByUniqueKey = async (params: any): Promise<any> => {
  try {
    const response = await invoicePort.post(
      "/api/invoice/generate/by-uniquekey",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error generating invoice:", error);
    throw new Error("Unable to generate the invoice. Please try again later.");
  }
};

export const downloadClientInvoice = async (
  invoiceId: number,
  sendEmailNotification?: boolean,
): Promise<void> => {
  try {
    const response = await invoicePort.post(
      "/api/invoice/generate/clientInvoice",
      { invoiceId, sendEmailNotification },
      {
        responseType: "arraybuffer",
        headers: {
          Accept: "application/pdf",
          "Content-Type": "application/json",
        },
      },
    );

    // Get filename from header
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "Invoice.pdf";

    if (contentDisposition) {
      const utf8FileNameMatch = contentDisposition.match(
        /filename\*=UTF-8''(.+)/,
      );
      if (utf8FileNameMatch?.length > 1) {
        fileName = decodeURIComponent(utf8FileNameMatch[1]);
      } else {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch?.length > 1) {
          fileName = fileNameMatch[1];
        }
      }
    }

    const blob = new Blob([response.data], {
      type: "application/pdf",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("PDF download failed:", error);
    throw error;
  }
};

export const downloadUserInvoice = async (
  invoiceId: number,
  sendEmailNotification?: boolean,
): Promise<void> => {
  try {
    const response = await invoicePort.post(
      "/api/invoice/generate/userInvoice",
      { invoiceId, sendEmailNotification },
      {
        responseType: "arraybuffer",
        headers: {
          Accept: "application/pdf",
          "Content-Type": "application/json",
        },
      },
    );

    // Extract filename from header
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "Invoice.pdf"; // fallback

    if (contentDisposition) {
      // First check for UTF-8 encoded filename*
      const utf8FileNameMatch = contentDisposition.match(
        /filename\*=UTF-8''(.+)/,
      );

      if (utf8FileNameMatch && utf8FileNameMatch.length > 1) {
        fileName = decodeURIComponent(utf8FileNameMatch[1]);
      } else {
        // Fallback to normal filename=
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }
    }

    const blob = new Blob([response.data], {
      type: "application/pdf",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("PDF download failed:", error);
    throw error;
  }
};

export const downloadRoleInvoice = async (
  invoiceId: number,
  sendEmailNotification?: boolean,
): Promise<void> => {
  try {
    const response = await invoicePort.post(
      "/api/invoice/generate/roleInvoice",
      { invoiceId, sendEmailNotification },
      {
        responseType: "arraybuffer",
        headers: {
          Accept: "application/pdf",
          "Content-Type": "application/json",
        },
      },
    );

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "Invoice.pdf"; // fallback

    if (contentDisposition) {
      // Check UTF-8 encoded filename*
      const utf8FileNameMatch = contentDisposition.match(
        /filename\*=UTF-8''(.+)/,
      );

      if (utf8FileNameMatch && utf8FileNameMatch.length > 1) {
        fileName = decodeURIComponent(utf8FileNameMatch[1]);
      } else {
        // Fallback normal filename=
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }
    }

    const blob = new Blob([response.data], {
      type: "application/pdf",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("PDF download failed:", error);
    throw error;
  }
};

export const getInvoiceSummaryCount = async (
  invoiceData: any,
): Promise<any> => {
  try {
    const response = await invoicePort.post<ApiResponse<any>>(
      "/api/invoice/line-items/status-summary",
      invoiceData,
    );

    return response.data?.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const fetchInvoiceNotesCategoryList = async (): Promise<any[]> => {
  try {
    const response = await invoicePort.get("/api/invoice/notes/categories");
    const list = response.data?.result.map((item: any) => ({
      value: item.id,
      label: item.notesCategoryName,
    }));
    return list;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const fetchSyncReProcessing = async (
  invoiceLineItemIdCsv: string,
): Promise<any> => {
  try {
    const response = await invoicePort.get(
      `/api/invoice/line-items/sync-reprocessing?invoiceLineItemIdCsv=${invoiceLineItemIdCsv}`,
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const fetchDocumentTypeListForStandardCharge = async () => {
  try {
    const response = await invoicePort.get(
      "/api/invoice/standard-charge-notes",
    );
    const list = response.data?.result
      ? [
          {
            value: response.data.result.documentTypeId,
            label: response.data.result.documentType,
          },
        ]
      : [];

    return list;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getStandardChargeNotesDetails = async (caseInfoId: number) => {
  try {
    const { data } = await invoicePort.get(
      `/api/invoice/standard-charge/note-details?caseInfoId=${caseInfoId}`,
    );

    const result = data?.result ?? [];
    const filterData = result?.map((item: any, index: number) => ({
      SrNo: index + 1,
      ...item,
      uploadedOn: `${formatAppointmentDateTime(item.uploadedOn)} | ${item.uploadedBy}`,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const updateInvoiceResolutionPageLines = async (
  params: any,
): Promise<any> => {
  try {
    const response = await invoicePort.post(
      "/api/invoice/update-invoiceexcres-pageslines",
      params,
    );

    return response?.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
